import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertCircleIcon, ChevronLeftIcon, EditIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';

import { DialogDeleteRecordConfirmation } from '../../../components/pages/backoffice/dialogs/delete-record.jsx';
import { BackOfficePermissionsWrapper } from '../../../components/pages/backoffice/permissions-wrapper.jsx';
import DecorativeInput from '../../../components/decorative-input.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';

export function CustomersPendingConsentShow() {
  const { isAdmin } = useSnapshot(authState);
  const { pendingConsentId } = useParams();

  const queryPendingConsentCustomer = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const { data: customer } = await msCatalystApi.get(
        `/customers/consent-requests/${pendingConsentId}`,
      );
      const customerData = customer.data;

      customerData.customer = JSON.parse(customerData.customer);

      return customerData;
    },
  });

  const mutationDeleteCustomerConsentRequest = useMutation({
    mutationFn: async () => {
      await msCatalystApi.delete(
        `/customers/consent-requests/${pendingConsentId}`,
      );
    },
    onError: (err) => {
      toast.error('Erro ao apagar Cliente Pendente de Consentimento');
    },
  });

  return queryPendingConsentCustomer.isLoading ? (
    <div className="flex h-[70dvh] items-center justify-center">
      <Loading />
    </div>
  ) : queryPendingConsentCustomer.isError ? (
    <Error />
  ) : !queryPendingConsentCustomer.data ? (
    <div className="flex flex-col items-center">
      <AlertCircleIcon className="size-20" />
      <h1 className="py-6 text-xl">
        O Consentimento Pendente de Cliente que procura não existe...
      </h1>
      <Button variant="link" asChild>
        <ChevronLeftIcon className="mr-2 size-4" />
        <Link href="/backoffice/customers/pending-consent">
          Voltar à página de Consentimento Pendentes
        </Link>
      </Button>
    </div>
  ) : (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 pt-4 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <div>
          <Heading className="pb-2">Cliente Pendente de Consentimento</Heading>
          <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
            {queryPendingConsentCustomer.data.fullName}
          </h2>
        </div>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          {isAdmin ? (
            <DialogDeleteRecordConfirmation
              recordName={`${queryPendingConsentCustomer.data.firstName} ${queryPendingConsentCustomer.data.lastName}`}
              redirect="/backoffice/customers"
              action={async () =>
                await mutationDeleteCustomerConsentRequest.mutateAsync()
              }
            />
          ) : null}

          <BackOfficePermissionsWrapper>
            <Link to="edit">
              <Button>
                <EditIcon />
                Editar
              </Button>
            </Link>
          </BackOfficePermissionsWrapper>
        </div>
      </div>

      <div className="flex flex-col space-y-10">
        <div>
          <h2 className="pb-5 text-lg text-zinc-600 dark:text-zinc-400">
            Dados do Pedido de Consentimento
          </h2>
          <div className="flex flex-col gap-5">
            <FormCard>
              <DecorativeInput
                value={
                  queryPendingConsentCustomer.data.action === 'create'
                    ? 'Criação de Conta'
                    : 'Atualização de Conta'
                }
                label="Ação"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.staff}
                label="Nome do Operador"
              />
            </FormCard>
          </div>
        </div>

        <div>
          <h2 className="pb-5 text-lg text-zinc-600 dark:text-zinc-400">
            Dados do Cliente
          </h2>
          <div className="flex flex-col gap-5">
            <FormCard>
              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.firstName}
                label="Nome"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.lastName}
                label="Apelido"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.cc}
                label="Nº Identificação"
              />

              <DecorativeInput
                value={format(
                  queryPendingConsentCustomer.data.customer.birthDate,
                  'dd-MM-yyyy',
                )}
                label="Data de Nascimento"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.email}
                label="Email"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.phone}
                label="Telemóvel"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.location}
                label="Localidade"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.address}
                label="Morada"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.district}
                label="Distrito"
              />

              <DecorativeInput
                value={queryPendingConsentCustomer.data.customer.zipCode}
                label="Código de Postal"
              />
            </FormCard>
          </div>
        </div>
      </div>

      <FormFooter>
        {isAdmin ? (
          <DialogDeleteRecordConfirmation
            recordName={queryPendingConsentCustomer.data.ROWID}
            redirect="/backoffice/customers/pending-consent"
            action={async () =>
              await mutationDeleteCustomerConsentRequest.mutateAsync()
            }
          />
        ) : null}

        <BackOfficePermissionsWrapper>
          <Link
            to={`/backoffice/customers/pending-consent/${pendingConsentId}/edit`}
          >
            <Button>
              <EditIcon />
              Editar
            </Button>
          </Link>
        </BackOfficePermissionsWrapper>
      </FormFooter>
    </>
  );
}
